import { useEffect, useState } from "react";
import apiClient, { formatApiError, mediaUrl } from "../api";
import { Input } from "../../components/ui/input";
import { Upload, Trash2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";

const MediaPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get("/admin/media");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await apiClient.post("/admin/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Afbeelding geüpload");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onDelete = async (m) => {
    if (!window.confirm(`"${m.original_filename}" verwijderen?`)) return;
    try {
      await apiClient.delete(`/admin/media/${m.id}`);
      toast.success("Verwijderd");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setEditForm({ alt_text: m.alt_text || "", title: m.title || "" });
  };
  const cancelEdit = () => setEditingId(null);

  const saveEdit = async (id) => {
    try {
      await apiClient.patch(`/admin/media/${id}`, editForm);
      toast.success("Bijgewerkt");
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-media-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <p className="text-[11px] tracking-[0.3em] uppercase text-champagne font-body mb-3">
            CMS · Media
          </p>
          <h1 className="font-display text-5xl text-white tracking-tight">
            Mediabibliotheek
          </h1>
          <p className="mt-3 text-sm text-zinc-400 font-body max-w-xl">
            Upload, beheer en hergebruik afbeeldingen voor de hele website.
            JPG, PNG en WebP — maximaal 10 MB per bestand.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 px-5 py-3 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[11px] tracking-[0.3em] uppercase font-body font-semibold cursor-pointer">
          <Upload size={13} />
          {uploading ? "Bezig..." : "Afbeelding uploaden"}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={onUpload}
            disabled={uploading}
            className="hidden"
            data-testid="media-upload-input"
          />
        </label>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">Laden...</div>
      ) : items.length === 0 ? (
        <div className="bg-ink-900 border border-white/[0.06] p-10 text-center text-sm text-zinc-500 font-body">
          Nog geen afbeeldingen geüpload.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map((m) => (
            <div
              key={m.id}
              data-testid={`media-card-${m.id}`}
              className="bg-ink-900 border border-white/[0.06]"
            >
              <div className="aspect-[4/3] bg-ink-950 overflow-hidden">
                <img
                  src={mediaUrl(m)}
                  alt={m.alt_text || m.original_filename}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              <div className="p-4 flex flex-col gap-3">
                {editingId === m.id ? (
                  <>
                    <Input
                      placeholder="Alt-tekst"
                      value={editForm.alt_text || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, alt_text: e.target.value }))
                      }
                      className="h-9 bg-ink-950 border-white/10 focus:border-champagne text-xs font-body text-white rounded-none"
                    />
                    <Input
                      placeholder="Titel (optioneel)"
                      value={editForm.title || ""}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, title: e.target.value }))
                      }
                      className="h-9 bg-ink-950 border-white/10 focus:border-champagne text-xs font-body text-white rounded-none"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => saveEdit(m.id)}
                        data-testid={`media-save-${m.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 py-2 bg-champagne text-ink-950 hover:bg-champagne-soft transition-colors text-[10px] tracking-[0.3em] uppercase font-body font-semibold"
                      >
                        <Save size={11} /> Opslaan
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="flex-1 inline-flex items-center justify-center gap-2 py-2 border border-white/10 text-zinc-300 hover:border-champagne hover:text-champagne transition-colors text-[10px] tracking-[0.3em] uppercase font-body"
                      >
                        <X size={11} /> Annuleer
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-xs text-white font-body truncate">
                      {m.original_filename}
                    </p>
                    <p className="text-[10px] text-zinc-500 font-body truncate">
                      {m.alt_text || "Geen alt-tekst"}
                    </p>
                    <div className="flex items-center justify-between text-[10px] text-zinc-600 font-body">
                      <span>{formatBytes(m.size)}</span>
                      <span>
                        {m.width && m.height
                          ? `${m.width}×${m.height}`
                          : m.mime_type.replace("image/", "").toUpperCase()}
                      </span>
                      <span>
                        {new Date(m.created_at).toLocaleDateString("nl-NL")}
                      </span>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => startEdit(m)}
                        data-testid={`media-edit-${m.id}`}
                        className="flex-1 inline-flex items-center justify-center gap-2 py-2 border border-white/10 text-zinc-300 hover:border-champagne hover:text-champagne transition-colors text-[10px] tracking-[0.3em] uppercase font-body"
                      >
                        <Pencil size={11} /> Bewerken
                      </button>
                      <button
                        type="button"
                        onClick={() => onDelete(m)}
                        data-testid={`media-delete-${m.id}`}
                        className="w-9 inline-flex items-center justify-center border border-white/10 text-zinc-400 hover:border-red-500/40 hover:text-red-400 transition-colors"
                        aria-label="Verwijderen"
                      >
                        <Trash2 size={11} />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const formatBytes = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

export default MediaPage;
